import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useSmartSSE } from '../hooks/useSmartSSE';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Heart, Share2, MoreHorizontal, Trash2, MessageCircle } from 'lucide-react';
import PostModal from './PostModal';
import HashtagText from './ui/HashtagText';

interface Post {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
  deleted?: boolean;
}

interface Comment {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Memoized Post Component to prevent unnecessary re-renders during scrolling
const PostItem = memo(({ 
  post, 
  user, 
  isLastPost, 
  lastPostElementRef, 
  openPostModal, 
  handleLikePost, 
  handleSharePost, 
  openDeleteDialog,
  removeDeletedPost,
  formatDate 
}: {
  post: Post;
  user: any;
  isLastPost: boolean;
  lastPostElementRef: (node: HTMLDivElement | null) => void;
  openPostModal: (post: Post) => void;
  handleLikePost: (postId: string) => void;
  handleSharePost: (post: Post, event: React.MouseEvent) => void;
  openDeleteDialog: (postId: string) => void;
  removeDeletedPost: (postId: string) => void;
  formatDate: (dateString: string) => string;
}) => {
  // If the post is soft-deleted, show a placeholder
  if (post.deleted) {
    return (
      <div
        key={post._id}
        ref={isLastPost ? lastPostElementRef : null}
        className="mb-4" // No cursor pointer or hover effects
        style={{ 
          transform: 'translateZ(0)', // Force hardware acceleration
          contain: 'layout style paint' // CSS containment for better performance
        }}
      >
        <Card className="bg-parchment-50 border-parchment-200 shadow-sm">
          <CardContent className="p-4 pt-2 pb-1 relative">
            {/* X button to remove deleted post */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                removeDeletedPost(post._id);
              }}
              className="absolute top-2 right-2 h-8 w-8 p-0 text-custom-blue-400 hover:text-custom-blue-600 hover:bg-parchment-200"
              title="Remove from feed"
            >
              ✕
            </Button>
            
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-custom-blue-400 mb-2">
                  <MessageCircle className="w-6 h-6 mx-auto opacity-50" />
                </div>
                <p className="text-sm text-custom-blue-400 italic">
                  Post deleted
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      key={post._id}
      ref={isLastPost ? lastPostElementRef : null}
      className="cursor-pointer hover:opacity-95 transition-opacity"
      onClick={() => openPostModal(post)}
      style={{ 
        transform: 'translateZ(0)', // Force hardware acceleration
        contain: 'layout style paint' // CSS containment for better performance
      }}
    >
      <Card className="mb-4 bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
        <CardContent className="p-4 pt-2 pb-1">
          {/* Flexible Layout - Similar to PostModal */}
          <div className="space-y-3">
            
            {/* Header: Profile Picture + Profile Info + Dropdown Menu */}
            <div className="flex items-center gap-3">
              <Link 
                to={`/@${post.user.handle}`}
                className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={post.user.picture} alt={post.user.name} />
                  <AvatarFallback className="bg-parchment-200 text-custom-blue-800">
                    {post.user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <Link 
                    to={`/@${post.user.handle}`}
                    className="cursor-pointer hover:text-custom-blue-700 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="font-semibold text-custom-blue-900 text-sm">
                      {post.user.name}
                    </span>
                  </Link>
                  <Link 
                    to={`/@${post.user.handle}`}
                    className="cursor-pointer hover:text-custom-blue-500 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-sm text-custom-blue-600">
                      @{post.user.handle}
                    </span>
                  </Link>
                  <span className="text-sm text-custom-blue-500">•</span>
                  <span className="text-sm text-custom-blue-500">
                    {formatDate(post.createdAt)}
                  </span>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                {user && user.handle === post.user.handle && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className="bg-paper border-parchment-300 z-[9999] shadow-lg"
                      sideOffset={5}
                    >
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(post._id);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete this post
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* Post Content */}
            <div className="ml-12 pr-4">
              <HashtagText 
                text={post.content}
                className="text-sm text-custom-blue-800 leading-relaxed whitespace-pre-wrap break-words text-left block"
              />
            </div>

            {/* Footer: Comment Count + Action Buttons */}
            <div className="flex items-center justify-between ml-12 pr-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openPostModal(post);
                }}
                className="h-8 p-2 text-sm text-custom-blue-700 hover:bg-parchment-100 flex items-center space-x-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>{post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}</span>
              </Button>

              <div className="flex items-center space-x-2">
                {user ? (
                  // Show interactive buttons for authenticated users
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikePost(post._id);
                      }}
                      className={`h-10 w-10 p-0 hover:bg-parchment-100 transition-colors ${post.isLiked ? 'text-red-500' : 'text-custom-blue-600'}`}
                    >
                      <Heart 
                        className="w-5 h-5" 
                        fill={post.isLiked ? 'currentColor' : 'none'} 
                      />
                    </Button>
                    {post.likeCount > 0 && (
                      <span className="text-sm text-gray-600 ml-1">
                        {post.likeCount}
                      </span>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100 transition-colors"
                      onClick={(e) => handleSharePost(post, e)}
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </>
                ) : (
                  // Show static view for non-authenticated users, but make share button functional
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 text-custom-blue-600">
                      <Heart className="w-5 h-5" fill="none" />
                      {post.likeCount > 0 && (
                        <span className="text-sm text-gray-600">
                          {post.likeCount}
                        </span>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-10 w-10 p-0 text-custom-blue-600 hover:bg-parchment-100 transition-colors"
                      onClick={(e) => handleSharePost(post, e)}
                    >
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

PostItem.displayName = 'PostItem';

const Feed: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { addToast } = useToast();

  // Scroll to top when navigating to this route
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Feed type state
  const [feedType, setFeedType] = useState<'following' | 'all'>('following');

  // Set non-authenticated users to 'all' feed type
  useEffect(() => {
    if (!user) {
      setFeedType('all');
    }
  }, [user]);

  const [posts, setPosts] = useState<Post[]>([]);
  // Modal state
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Separate state for posts fetched by ID that shouldn't be permanently added to feed
  const [fetchedPostsById, setFetchedPostsById] = useState<{[postId: string]: Post}>({});
  
  // Replacing state with refs to prevent re-renders when liking comments
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const initialFetchRef = useRef(false);
  const [page, setPage] = useState(1);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const postsPerPage = 20;const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  
  // Using state for the shared post content across mobile and desktop
  const [newPostContent, setNewPostContent] = useState('');
  
  // Mobile post creation expansion state
  const [isMobilePostExpanded, setIsMobilePostExpanded] = useState(false);
  
  // Use refs for immediate typing without re-renders
  const mobileTextareaRef = useRef<HTMLTextAreaElement>(null);
  const desktopTextareaRef = useRef<HTMLTextAreaElement>(null);
  const lastSyncedContent = useRef('');
  
  // State for character count display
  const [characterCount, setCharacterCount] = useState(0);
  
  // SSE and new posts state
  const [pendingPosts, setPendingPosts] = useState<Post[]>([]);
  const [showNewPostsButton, setShowNewPostsButton] = useState(false);
  const MAX_PENDING_POSTS = 50; // Limit pending posts to prevent memory issues
  
  // State for PostModal comments from SSE
  const [modalComments, setModalComments] = useState<Comment[]>([]);
  
  // Sync content when switching between mobile and desktop views
  useEffect(() => {
    // Update character count when content changes
    setCharacterCount(newPostContent.length);
  }, [newPostContent]);
  
  // Sync content from active textarea to state only when needed
  const syncContentToState = useCallback(() => {
    // Since we're using controlled components, the state is already synced
    // This function is kept for compatibility with existing handlers
    return;
  }, []);
  
  // Memoize feed type handlers to prevent unnecessary re-renders
  const handleFollowingFeedClick = useCallback(() => {
    if (feedType !== 'following') {
      setFeedType('following');
    }
  }, [feedType]);
  
  const handleAllPostsFeedClick = useCallback(() => {
    if (feedType !== 'all') {
      setFeedType('all');
    }
  }, [feedType]);
  const handleMobilePostToggle = useCallback(() => {
    syncContentToState(); // Sync content before toggling
    setIsMobilePostExpanded(prev => !prev);
  }, [syncContentToState]);
  
  const handleMobilePostClose = useCallback(() => {
    syncContentToState(); // Sync content before closing
    setIsMobilePostExpanded(false);
  }, [syncContentToState]);

  // SSE handlers
  const handleNewPost = useCallback((newPost: Post) => {
    // Check if the current user is the author of this post
    const isCurrentUserPost = user && user.handle === newPost.user.handle;
    
    if (isCurrentUserPost) {
      // If it's the current user's post, add it directly to the feed
      setPosts(prevPosts => {
        // Check if post already exists to prevent duplicates
        const existingPost = prevPosts.find(p => p._id === newPost._id);
        if (existingPost) {
          return prevPosts;
        }
        return [newPost, ...prevPosts];
      });
      // Scroll to top to show the new post
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // For other users' posts, add to pending posts with limit
      setPendingPosts(prev => {
        // Check if post already exists to prevent duplicates
        const existingPost = prev.find(p => p._id === newPost._id);
        if (existingPost) {
          return prev;
        }
        
        // Add new post and limit the array size
        const newPending = [newPost, ...prev];
        return newPending.slice(0, MAX_PENDING_POSTS);
      });
      setShowNewPostsButton(true);
    }
  }, [user, MAX_PENDING_POSTS]);

  const handlePostUpdate = useCallback((updatedPost: Post) => {
    // Update existing posts in the feed
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post._id === updatedPost._id ? updatedPost : post
      )
    );
    
    // Also update posts in pending posts if they exist there
    setPendingPosts(prevPending => 
      prevPending.map(post => 
        post._id === updatedPost._id ? updatedPost : post
      )
    );
    
    // Also update the selected post if it matches
    setSelectedPost(prevSelected => {
      if (prevSelected && prevSelected._id === updatedPost._id) {
        return updatedPost;
      }
      return prevSelected;
    });
    
    // Also update in fetched posts if present
    setFetchedPostsById(prevFetched => {
      if (prevFetched[updatedPost._id]) {
        return {
          ...prevFetched,
          [updatedPost._id]: updatedPost
        };
      }
      return prevFetched;
    });
  }, []);

  const handleNewComment = useCallback((postId: string, comment: Comment) => {
    // If the PostModal is open for this post, add the comment to the modal comments
    if (isModalOpen && selectedPost && selectedPost._id === postId) {
      setModalComments(prevComments => [...prevComments, comment]);
    }
  }, [isModalOpen, selectedPost]);

  const handlePostDeleted = useCallback((postId: string) => {
    // Mark post as deleted in current posts (soft delete)
    setPosts(prevPosts => prevPosts.map(post => 
      post._id === postId 
        ? { ...post, deleted: true }
        : post
    ));
    
    // Remove from pending posts (this fixes the issue where deleted posts still appear in "X new posts available")
    setPendingPosts(prevPending => prevPending.filter(post => post._id !== postId));
    
    // Update the "new posts available" button count
    setPendingPosts(prevPending => {
      if (prevPending.length === 0) {
        setShowNewPostsButton(false);
      }
      return prevPending;
    });
    
    // If the deleted post was open in modal, close the modal
    if (selectedPost && selectedPost._id === postId) {
      setIsModalOpen(false);
      setSelectedPost(null);
      setModalComments([]);
      window.history.replaceState(null, '', '/feed');
    }
    
    // Remove from fetched posts by ID
    setFetchedPostsById(prevFetched => {
      const updated = { ...prevFetched };
      delete updated[postId];
      return updated;
    });
  }, [selectedPost]);

  const showNewPosts = useCallback(() => {
    // Add pending posts to the top of the feed and remove deleted posts
    setPosts(prevPosts => {
      // Filter out deleted posts from existing feed
      const nonDeletedPosts = prevPosts.filter(post => !post.deleted);
      
      // Add new posts at the top
      return [...pendingPosts, ...nonDeletedPosts];
    });
    setPendingPosts([]);
    setShowNewPostsButton(false);
    
    // Scroll to top to show new posts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pendingPosts]);  // Sync refs with state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Create a stable reference to current feedType
  const feedTypeRef = useRef<'following' | 'all'>('following');
  
  // Update ref when feedType changes
  useEffect(() => {
    feedTypeRef.current = feedType;
  }, [feedType]);

  // Fetch posts from backend with pagination - stable function that doesn't change
  const fetchPosts = useCallback(async (pageNum: number = 1, append: boolean = false, targetFeedType?: 'following' | 'all') => {
    if (loadingRef.current) {
      return;
    }
    
    // Use targetFeedType if provided, otherwise use ref value
    const currentFeedType = targetFeedType || feedTypeRef.current;
    
    setLoading(true);
    loadingRef.current = true;
    try {
      // Build URL with cursor-based pagination for subsequent pages
      let url = `${process.env.REACT_APP_API_URL}/api/posts?limit=${postsPerPage}`;
      
      // Add following parameter
      if (currentFeedType === 'following') {
        url += '&following=true';
      }
      
      if (append && nextCursor) {
        // Use cursor-based pagination for loading more posts
        url += `&cursor=${encodeURIComponent(nextCursor)}`;
      } else {
        // Use page-based pagination for initial load or page navigation
        url += `&page=${pageNum}`;
      }
      
      // Add authorization header for authenticated requests
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        const newPosts = data.data.posts || [];
        const pagination = data.data.pagination;
        
        // Update cursor for next request
        setNextCursor(pagination?.nextCursor || null);
        
        if (append) {
          // Filter out duplicates by checking existing post IDs
          setPosts(prevPosts => {
            const existingIds = new Set(prevPosts.map(post => post._id));
            const uniqueNewPosts = newPosts.filter((post: Post) => !existingIds.has(post._id));
            return [...prevPosts, ...uniqueNewPosts];
          });
          
          // If we're appending and got no new posts, there are no more posts to load
          if (newPosts.length === 0) {
            setHasMore(false);
            hasMoreRef.current = false;
          } else {
            const newHasMore = pagination?.hasNextPage || false;
            setHasMore(newHasMore);
            hasMoreRef.current = newHasMore;
          }
        } else {
          setPosts(newPosts);
          const newHasMore = pagination?.hasNextPage || false;
          setHasMore(newHasMore);
          hasMoreRef.current = newHasMore;
        }
      } else {
        console.error('API error:', data.message);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [nextCursor, postsPerPage]);

  // Handle reconnection after inactivity - refresh feed to catch up on missed updates
  const handleReconnect = useCallback(async (inactiveTimestamp?: Date | null) => {
    try {
      // Fetch fresh posts without updating the current feed
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      let url = `${process.env.REACT_APP_API_URL}/api/posts?limit=${postsPerPage}&page=1`;
      
      // Add following parameter
      if (feedType === 'following') {
        url += '&following=true';
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      const data = await response.json();

      if (data.status === 'success') {
        const freshPosts: Post[] = data.data.posts || [];
        
        // Create maps for easy lookup
        const currentPostIds = new Set(posts.map(post => post._id));
        const freshPostsMap = new Map<string, Post>(freshPosts.map((post: Post) => [post._id, post]));
        
        // Find posts that are truly new (not in current feed AND created after user went inactive)
        const newPosts = freshPosts.filter((post: Post) => {
          const isNotInCurrentFeed = !currentPostIds.has(post._id);
          
          // If we have an inactive timestamp, only consider posts created after that time as "new"
          if (inactiveTimestamp && isNotInCurrentFeed) {
            const postCreatedAt = new Date(post.createdAt);
            const isCreatedAfterInactive = postCreatedAt > inactiveTimestamp;
            return isCreatedAfterInactive;
          }
          
          // If no timestamp, fall back to the old behavior
          return isNotInCurrentFeed;
        });
        
        // Clean up and update pending posts with fresh data
        setPendingPosts(prevPending => {
          const freshPostIds = new Set(freshPosts.map((post: Post) => post._id));
          const freshPostsMap = new Map<string, Post>(freshPosts.map((post: Post) => [post._id, post]));
          
          // Filter out deleted posts and update existing pending posts with fresh data
          const updatedPendingPosts = prevPending
            .filter(post => freshPostIds.has(post._id)) // Remove deleted posts
            .map(post => {
              const freshPost = freshPostsMap.get(post._id);
              if (freshPost) {
                // Update pending post with fresh like/comment counts
                return {
                  ...post,
                  likeCount: freshPost.likeCount,
                  commentCount: freshPost.commentCount,
                  isLiked: freshPost.isLiked
                };
              }
              return post;
            });
          
          return updatedPendingPosts;
        });
        
        // Update existing posts with fresh like/comment counts
        // Also mark posts as deleted if they're not found in fresh data
        setPosts(prevPosts => 
          prevPosts.map(post => {
            const freshPost = freshPostsMap.get(post._id) as Post | undefined;
            if (freshPost) {
              // Update with fresh data (like counts, comment counts, etc.)
              // but preserve deleted state
              return {
                ...post,
                likeCount: freshPost.likeCount,
                commentCount: freshPost.commentCount,
                isLiked: freshPost.isLiked,
                deleted: post.deleted || false // Keep deleted state
              };
            } else {
              // Post not found in fresh data - it was likely deleted
              // Mark as deleted if it wasn't already
              if (!post.deleted) {
                return {
                  ...post,
                  deleted: true
                };
              }
              return post;
            }
          })
        );
        
        // Also update selectedPost if it's open in modal
        setSelectedPost(prevSelected => {
          if (prevSelected) {
            const freshPost = freshPostsMap.get(prevSelected._id) as Post | undefined;
            if (freshPost) {
              return {
                ...prevSelected,
                likeCount: freshPost.likeCount,
                commentCount: freshPost.commentCount,
                isLiked: freshPost.isLiked
              };
            }
          }
          return prevSelected;
        });
        
        if (newPosts.length > 0) {
          // Add new posts to pending posts to show "X new posts available" button
          setPendingPosts(prevPending => {
            // Avoid duplicates by filtering out posts already in pending
            const pendingIds = new Set(prevPending.map(p => p._id));
            const uniqueNewPosts = newPosts.filter((post: Post) => !pendingIds.has(post._id));
            return [...uniqueNewPosts, ...prevPending];
          });
        }
        
        // Update "new posts available" button visibility
        setPendingPosts(prevPending => {
          setShowNewPostsButton(prevPending.length > 0);
          return prevPending;
        });
        
      }
    } catch (error) {
      console.error('Error fetching fresh posts after reconnection:', error);
    }
  }, [feedType, posts, postsPerPage]);

  // Initialize SSE
  const { isActive, connectionStatus, forceReconnect } = useSmartSSE({
    onNewPost: handleNewPost,
    onPostUpdate: handlePostUpdate,
    onNewComment: handleNewComment,
    onPostDeleted: handlePostDeleted,
    feedType,
    onReconnect: handleReconnect
  });

  // Fetch a single post by ID
  const fetchPostById = useCallback(async (postId: string): Promise<Post | null> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/${postId}`, {
        method: 'GET',
        headers: headers,
      });

      const data = await response.json();

      if (data.status === 'success' && data.data) {
        return data.data;
      } else {
        console.error('Failed to fetch post:', data.message);
        return null;
      }
    } catch (error) {
      console.error('Error fetching post by ID:', error);
      return null;
    }
  }, []);

  const loadMorePosts = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    
    // For cursor-based pagination, we don't increment page number
    // The cursor is handled in fetchPosts
    fetchPosts(page, true, feedTypeRef.current);
  }, [page, fetchPosts]);
  const lastPostElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingRef.current) return;
      if (observer.current) observer.current.disconnect();
      
      // Optimized intersection observer with threshold and margins
      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
            // Use requestIdleCallback for better performance if available
            if (window.requestIdleCallback) {
              window.requestIdleCallback(() => loadMorePosts());
            } else {
              setTimeout(() => loadMorePosts(), 0);
            }
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px 0px', // Start loading before the element is fully visible
        }
      );
      
      if (node) observer.current.observe(node);
    },
    [loadMorePosts]
  );  // Initial fetch - only run once
  useEffect(() => {
    if (!initialFetchRef.current) {
      initialFetchRef.current = true;
      fetchPosts(1, false, 'following'); // Explicitly pass initial feed type
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - truly run only once

  // Handle feed type changes separately
  useEffect(() => {
    // Only run if we've already done the initial fetch
    if (initialFetchRef.current) {
      // Reset pagination state
      setPage(1);
      setNextCursor(null);
      setHasMore(true);
      hasMoreRef.current = true;
      setPosts([]);
      
      // Fetch new posts for the selected feed type
      fetchPosts(1, false, feedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedType]); // fetchPosts intentionally excluded to prevent infinite loop // Handle URL parameters for opening specific posts
  const processedPostRef = useRef<string | null>(null);
  const isClosingModalRef = useRef(false);

  useEffect(() => {
    // If modal is in the process of closing, don't process URL changes
    if (isClosingModalRef.current) {
      return;
    }

    // Get the current URL pathname - use window.location.pathname instead of location.pathname
    // because location.pathname might be stale when this effect runs
    const currentPathname = window.location.pathname;
    const postUrlMatch = currentPathname.match(/^\/@([^/]+)\/post\/([a-f0-9]{24})$/);
    
    if (postUrlMatch) {
      const [, , postId] = postUrlMatch;
      
      // Only process if this is a different post than what we have processed AND the modal isn't already open for this post
      // This handles direct URL navigation and page refreshes, not programmatic modal opening
      if (postId !== processedPostRef.current && (!isModalOpen || selectedPost?._id !== postId)) {
        // Check if we already have this post in our current posts
        let existingPost = posts.find(p => p._id === postId);
        
        // If not in main posts, check if we already fetched it by ID
        if (!existingPost) {
          existingPost = fetchedPostsById[postId];
        }
        
        if (existingPost) {
          processedPostRef.current = postId;
          setSelectedPost(existingPost);
          setIsModalOpen(true);
        } else {
          // Post not found, try to fetch the post by ID from the backend
          processedPostRef.current = postId;
          
          fetchPostById(postId).then(fetchedPost => {
            if (fetchedPost) {
              // Store the fetched post separately (not in main feed)
              setFetchedPostsById(prev => ({
                ...prev,
                [postId]: fetchedPost
              }));
              
              // Open the modal with the fetched post
              setSelectedPost(fetchedPost);
              setIsModalOpen(true);
            } else {
              addToast('Post not found or no longer available', 'error');
              window.history.replaceState(null, '', '/feed');
              processedPostRef.current = null;
            }
          }).catch(error => {
            console.error('Error fetching post by ID for direct URL:', error);
            addToast('Failed to load post', 'error');
            window.history.replaceState(null, '', '/feed');
            processedPostRef.current = null;
          });
        }
      }
    } else {
      // Not on a post URL - if modal is open, we should close it
      // BUT only if we're not in the process of opening a modal programmatically
      // Check if processedPostRef matches the current modal post to avoid closing during programmatic opening
      if (isModalOpen && processedPostRef.current !== selectedPost?._id) {
        setIsModalOpen(false);
        setSelectedPost(null);
        processedPostRef.current = null;
        
        // Clean up any fetched posts
        const fetchedPostIds = Object.keys(fetchedPostsById);
        if (fetchedPostIds.length > 0) {
          setFetchedPostsById({});
        }
      }
    }
  }, [location.pathname, posts, addToast, isModalOpen, fetchPostById, fetchedPostsById, selectedPost]);

  // Handle cleanup of fetched posts when modal closes
  useEffect(() => {
    // Only clean up fetched posts when modal is closed
    if (!isModalOpen && processedPostRef.current === null) {
      // Check if there are any fetched posts to clean up
      const fetchedPostIds = Object.keys(fetchedPostsById);
      if (fetchedPostIds.length > 0) {
        setFetchedPostsById({});
      }
    }
  }, [isModalOpen, fetchedPostsById]);
  const handleLikePost = async (postId: string) => {
    if (!user) {
      addToast('Please log in to like posts', 'error');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      // Find the current post to determine if it's liked
      let currentPost = posts.find(p => p._id === postId);
      
      // If not found in main posts, check fetched posts
      if (!currentPost) {
        currentPost = fetchedPostsById[postId];
      }
      
      if (!currentPost) return;

      const isCurrentlyLiked = currentPost.isLiked;
      const url = `${process.env.REACT_APP_API_URL}/api/posts/${postId}/like`;
      const method = isCurrentlyLiked ? 'DELETE' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update like status');
      }

      const result = await response.json();

      // Update the posts state (main feed)
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post._id === postId 
            ? {
                ...post,
                likeCount: result.data.likeCount,
                isLiked: result.data.isLiked
              }
            : post
        )
      );
      
      // Also update fetched posts if this post is in there
      if (fetchedPostsById[postId]) {
        setFetchedPostsById(prevFetched => ({
          ...prevFetched,
          [postId]: {
            ...prevFetched[postId],
            likeCount: result.data.likeCount,
            isLiked: result.data.isLiked
          }
        }));
      }

    } catch (error) {
      console.error('Error updating like status:', error);
      addToast(
        error instanceof Error ? error.message : 'Failed to update like status', 
        'error'
      );
    }
  };

  const handlePublishPost = useCallback(async () => {
    const postText = newPostContent.trim();
    
    if (!postText) {
      addToast('Please enter some content for your post', 'error');
      return;
    }

    setIsCreatingPost(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      // Add Authorization header if we have a session token
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: postText
        })
      });

      if (response.ok) {
        addToast('Post shared successfully!', 'success');
        
        // Clear content and state
        setNewPostContent('');
        lastSyncedContent.current = '';
        setCharacterCount(0);
        
        // Collapse mobile post creation after successful post
        setIsMobilePostExpanded(false);
        
        // Refresh posts to show the new post and reset pagination
        setNextCursor(null);
        await fetchPosts(1, false, feedTypeRef.current);
        setPage(1);
      } else {
        const errorData = await response.json();
        addToast(errorData.message || 'Failed to create post', 'error');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      addToast('An error occurred while creating your post', 'error');
    } finally {
      setIsCreatingPost(false);
    }
  }, [newPostContent, addToast, setIsCreatingPost, setIsMobilePostExpanded, setNextCursor, fetchPosts, feedTypeRef, setPage]);  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      // Add Authorization header if we have a session token
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/${postToDelete}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        addToast('Post deleted successfully!', 'success');
        
        // Remove the post from the local state
        setPosts(prevPosts => prevPosts.filter(post => post._id !== postToDelete));
        
        // Close the dialog
        setDeleteDialogOpen(false);
        setPostToDelete(null);
      } else {
        const errorData = await response.json();
        addToast(errorData.message || 'Failed to delete post', 'error');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      addToast('An error occurred while deleting the post', 'error');
    }
  };

  const openDeleteDialog = (postId: string) => {
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  // Remove deleted post from feed (just for UI cleanup)
  const removeDeletedPost = (postId: string) => {
    setPosts(prevPosts => prevPosts.filter(post => post._id !== postId));
  };

  // Modal handlers
  const openPostModal = (post: Post) => {
    // Clear the closing flag since we're explicitly opening a modal
    isClosingModalRef.current = false;
    
    // Set processed ref FIRST, before any state updates
    processedPostRef.current = post._id;
    
    // Update URL synchronously BEFORE state updates
    const currentPostUrl = `/@${post.user.handle}/post/${post._id}`;
    window.history.replaceState(null, '', currentPostUrl);
    
    // Use flushSync to force immediate state updates
    flushSync(() => {
      // Set modal state immediately - this is for programmatic opening
      setSelectedPost(post);
      setIsModalOpen(true);
    });
  };
  const closePostModal = () => {
    // Store the current post ID for cleanup before resetting
    const closedPostId = selectedPost?._id;
    
    // Set closing flag to prevent immediate reopening
    isClosingModalRef.current = true;
    
    // Reset processedPostRef immediately to prevent reopening
    processedPostRef.current = null;
    
    // Clean up fetched post if it was fetched separately
    if (closedPostId && fetchedPostsById[closedPostId]) {
      setFetchedPostsById(prev => {
        const updated = { ...prev };
        delete updated[closedPostId];
        return updated;
      });
    }
    
    // Close the modal immediately
    setIsModalOpen(false);
    setSelectedPost(null);
    
    // Clear modal comments
    setModalComments([]);
    
    // Navigate to feed
    window.history.replaceState(null, '', '/feed');
    
    // Clear the closing flag after a brief delay
    setTimeout(() => {
      isClosingModalRef.current = false;
    }, 100);
  };

  // Share functionality
  const handleSharePost = async (post: Post, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const postUrl = `${window.location.origin}/@${post.user.handle}/post/${post._id}`;
    
    try {
      await navigator.clipboard.writeText(postUrl);
      addToast('Post link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      
      // Fallback: show the URL in a prompt
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
        // On mobile, we'll show a different message
        addToast('Link ready to share: tap and hold to copy', 'info');
      } else {
        addToast('Unable to copy automatically. Please copy manually.', 'error');
      }
      
      // As a last resort, try to select the URL text if we can create a temporary element
      try {
        const tempElement = document.createElement('textarea');
        tempElement.value = postUrl;
        document.body.appendChild(tempElement);
        tempElement.select();
        document.execCommand('copy');
        document.body.removeChild(tempElement);
        addToast('Post link copied to clipboard!', 'success');
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    }
  };

  const handlePostUpdateFromModal = (updatedPost: Post) => {
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post._id === updatedPost._id ? updatedPost : post
      )
    );
  };

  const handlePostDeleteFromModal = (postId: string) => {
    setPosts(prevPosts => prevPosts.filter(post => post._id !== postId));
    closePostModal();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex flex-col min-h-screen py-2" style={{ willChange: 'transform' }}>
      <main className="flex flex-col w-full flex-1 px-4 md:px-20">
        <div className="w-full mt-6 max-w-7xl mx-auto">
          
          {/* Feed Type Toggle - Desktop Only - Only show for authenticated users */}
          {user && (
            <div className="mb-6 hidden md:block">
              <div className="flex bg-parchment-100 rounded-lg p-1 max-w-sm mx-auto">
                <button
                  onClick={handleFollowingFeedClick}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                    feedType === 'following'
                      ? 'bg-custom-blue-600 text-white shadow-sm'
                      : 'text-custom-blue-700 hover:text-custom-blue-800 hover:bg-parchment-200'
                  }`}
                >
                  Following
                </button>
                <button
                  onClick={handleAllPostsFeedClick}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                    feedType === 'all'
                      ? 'bg-custom-blue-600 text-white shadow-sm'
                      : 'text-custom-blue-700 hover:text-custom-blue-800 hover:bg-parchment-200'
                  }`}
                >
                  All Posts
                </button>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex flex-col md:flex-row w-full gap-6">
            {/* Create Post Section - Mobile First */}
            <div className="md:hidden">
              {user ? (
                // Interactive post creation for authenticated users
                <>
                  {!isMobilePostExpanded ? (
                    // Collapsed state - just a button
                    <div className="mb-6">
                      <Button
                        onClick={handleMobilePostToggle}
                        className="w-full bg-parchment-100 hover:bg-parchment-200 text-custom-blue-700 border border-parchment-300 h-12 text-left justify-start font-normal"
                        variant="outline"
                      >
                        📝 Tap to make a writing progress post
                      </Button>
                    </div>
                  ) : (
                    // Expanded state - full post creation card
                    <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900 mb-6">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-custom-blue-900">Share Your Progress</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleMobilePostClose}
                            className="h-8 w-8 p-0 text-custom-blue-600 hover:bg-parchment-100"
                          >
                            ✕
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <textarea
                          ref={mobileTextareaRef}
                          placeholder="What writing progress did you make today?"
                          value={newPostContent}
                          maxLength={500}
                          className="flex min-h-[80px] w-full rounded-md border border-parchment-300 bg-paper px-3 py-2 text-sm text-custom-blue-900 placeholder-custom-blue-500"
                          autoFocus
                          onChange={(e) => {
                            // Update state and character count immediately
                            setNewPostContent(e.target.value);
                            setCharacterCount(e.target.value.length);
                            lastSyncedContent.current = e.target.value;
                          }}
                        />
                        <div className="text-xs text-custom-blue-600 mt-1">
                          {characterCount}/500 characters
                        </div>
                        <Button 
                          className="mt-4 bg-custom-blue-600 hover:bg-custom-blue-700 text-white" 
                          onClick={handlePublishPost}
                          disabled={isCreatingPost}
                        >
                          {isCreatingPost ? 'Sharing...' : 'Share Post'}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Feed Type Toggle - Mobile Only for authenticated users */}
                  <div className="mb-6">
                    <div className="flex bg-parchment-100 rounded-lg p-1 max-w-sm mx-auto">
                      <button
                        onClick={handleFollowingFeedClick}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          feedType === 'following'
                            ? 'bg-custom-blue-600 text-white shadow-sm'
                            : 'text-custom-blue-700 hover:text-custom-blue-800 hover:bg-parchment-200'
                        }`}
                      >
                        Following
                      </button>
                      <button
                        onClick={handleAllPostsFeedClick}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                          feedType === 'all'
                            ? 'bg-custom-blue-600 text-white shadow-sm'
                            : 'text-custom-blue-700 hover:text-custom-blue-800 hover:bg-parchment-200'
                        }`}
                      >
                        All Posts
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Sign-in call-to-action for non-authenticated users
                <div className="mb-6">
                  <Link
                    to="/signin"
                    className="w-full bg-parchment-100 hover:bg-parchment-200 text-custom-blue-700 border border-parchment-300 h-12 text-left justify-start font-normal px-4 py-3 rounded-md transition-colors flex items-center"
                  >
                    📝 Sign In to Post
                  </Link>
                </div>
              )}
            </div>

            {/* Social Feed */}
            <div className="w-full md:w-2/3 md:pr-4">
              {/* New Posts Available Button */}
              {showNewPostsButton && (
                <div className="sticky top-0 z-10 mb-4">
                  <Button
                    onClick={showNewPosts}
                    className="w-full bg-custom-blue-600 hover:bg-custom-blue-700 text-white py-3 rounded-lg shadow-lg border-2 border-custom-blue-500 transition-all duration-200 hover:shadow-xl"
                  >
                    {pendingPosts.length} new post{pendingPosts.length !== 1 ? 's' : ''} available
                  </Button>
                </div>
              )}
              
              {/* Connection Status Indicator */}
              {connectionStatus === 'reconnecting' && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                  <span className="text-yellow-800 text-sm">Reconnecting to live updates...</span>
                  <Button
                    onClick={forceReconnect}
                    size="sm"
                    variant="outline"
                    className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
                  >
                    Retry
                  </Button>
                </div>
              )}
              
              {!isActive && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-blue-800 text-sm">Live updates paused due to inactivity</span>
                </div>
              )}

              {posts.map((post, index) => {
                const isLastPost = index === posts.length - 1;
                return (
                  <PostItem
                    key={post._id}
                    post={post}
                    user={user}
                    isLastPost={isLastPost}
                    lastPostElementRef={lastPostElementRef}
                    openPostModal={openPostModal}
                    handleLikePost={handleLikePost}
                    handleSharePost={handleSharePost}
                    openDeleteDialog={openDeleteDialog}
                    removeDeletedPost={removeDeletedPost}
                    formatDate={formatDate}
                  />
                );
              })}
            {loading && <div className="text-custom-blue-700">Loading more posts...</div>}
            {!hasMore && posts.length > 0 && <div className="text-custom-blue-600">No more posts to load.</div>}
            {posts.length === 0 && !loading && (
              <Card className="bg-paper border-parchment-300 text-custom-blue-900 shadow-paper">
                <CardContent className="text-center py-12">
                  {feedType === 'following' ? (
                    <>
                      <p className="text-lg mb-4">📚 Your Following Feed is Empty</p>
                      <p className="text-custom-blue-700 mb-4">
                        You're not following any authors yet. Follow other writers to see their progress updates here!
                      </p>
                      <Button
                        onClick={handleAllPostsFeedClick}
                        className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                      >
                        Browse All Posts
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-lg mb-4">📚 Welcome to Your Writing Feed!</p>
                      <p className="text-custom-blue-700">This is where you'll see writing progress updates from authors in your network.</p>
                      <p className="text-sm mt-4 text-custom-blue-600">Posts will appear here once authors start sharing their progress!</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Create Post Sidebar - Desktop Only */}
          <div className="hidden md:block w-1/3">
            {user ? (
              // Interactive post creation for authenticated users
              <>
                <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
                  <CardHeader>
                    <CardTitle className="text-custom-blue-900">Share Your Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      ref={desktopTextareaRef}
                      placeholder="What writing progress did you make today?"
                      value={newPostContent}
                      maxLength={500}
                      className="flex min-h-[80px] w-full rounded-md border border-parchment-300 bg-paper px-3 py-2 text-sm text-custom-blue-900 placeholder-custom-blue-500"
                      onChange={(e) => {
                        // Update state and character count immediately
                        setNewPostContent(e.target.value);
                        setCharacterCount(e.target.value.length);
                        lastSyncedContent.current = e.target.value;
                      }}
                    />
                    <div className="text-xs text-custom-blue-600 mt-1">
                      {characterCount}/500 characters
                    </div>
                    <Button 
                      className="mt-4 bg-custom-blue-600 hover:bg-custom-blue-700 text-white" 
                      onClick={handlePublishPost}
                      disabled={isCreatingPost}
                    >
                      {isCreatingPost ? 'Sharing...' : 'Share Post'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="mt-6 bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
                  <CardHeader>
                    <CardTitle className="text-custom-blue-900">Writing Community</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-custom-blue-700">
                      <p>• Share your daily writing progress</p>
                      <p>• Connect with fellow authors</p>
                      <p>• Get inspired by others' achievements</p>
                      <p>• Celebrate milestones together</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              // Sign-in call-to-action for non-authenticated users
              <>
                <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
                  <CardHeader>
                    <CardTitle className="text-custom-blue-900">Share Your Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex min-h-[80px] w-full rounded-md border border-parchment-300 bg-parchment-50 px-3 py-2 text-sm text-custom-blue-600 items-center justify-center">
                      <p className="text-center">What writing progress did you make today?</p>
                    </div>
                    <Link 
                      to="/signin"
                      className="mt-4 inline-block bg-custom-blue-600 hover:bg-custom-blue-700 text-white px-4 py-2 rounded-md transition-colors font-medium"
                    >
                      Sign In to Post
                    </Link>
                  </CardContent>
                </Card>

                {/* Info Card for non-authenticated users */}
                <Card className="mt-6 bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
                  <CardHeader>
                    <CardTitle className="text-custom-blue-900">Writing Community</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-custom-blue-700">
                      <p>• Share your daily writing progress</p>
                      <p>• Connect with fellow authors</p>
                      <p>• Get inspired by others' achievements</p>
                      <p>• Celebrate milestones together</p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-parchment-200">
                      <Link 
                        to="/signin"
                        className="w-full bg-custom-blue-600 hover:bg-custom-blue-700 text-white px-4 py-2 rounded-md transition-colors font-medium text-center block"
                      >
                        Join Our Community
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
        </div>
      </main>      {/* Delete Post Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-paper border-parchment-300 text-custom-blue-900">
          <DialogHeader>
            <DialogTitle className="text-custom-blue-900">Delete Post</DialogTitle>            <DialogDescription className="text-custom-blue-700">
              Are you sure you want to delete this post? This action cannot be undone and will also delete all comments on this post.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={closeDeleteDialog}
              className="border-parchment-300 text-custom-blue-700 hover:bg-parchment-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePost}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Modal */}
      <PostModal
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={closePostModal}
        onPostUpdate={handlePostUpdateFromModal}
        onPostDelete={handlePostDeleteFromModal}
        externalComments={modalComments}
        onExternalCommentAdded={(comment) => setModalComments(prev => [...prev, comment])}
        onExternalCommentDeleted={(commentId) => setModalComments(prev => prev.filter(c => c._id !== commentId))}
      />
    </div>
  );
};

export default Feed;
